import * as React from 'react';
import * as _ from 'lodash';
import { TableColumnWidget } from './TableColumnWidget';
import { observer } from 'mobx-react';
import { MouseEvent } from 'react';
import { TableRowsWidget } from './TableRowsWidget';
import { styled } from '../../theme';
import { LoaderSpinner } from '../LoaderWidget';

export interface AccessColumnEvent<T extends TableRow = TableRow> {
  cell: any;
  row: T;
  active?: boolean;
}

export enum TableColumnStyle {
  PRIMARY = 'primary',
  NORMAL = 'normal',
  SECONDARY = 'secondary'
}

export interface TableColumn<T extends TableRow = TableRow> {
  display: string;
  key: string;
  right?: boolean;
  accessor?: (event: AccessColumnEvent<T>) => JSX.Element | string;
  noWrap?: boolean;
  shrink?: boolean;
  style?: TableColumnStyle;
}

export interface TableRow {
  cells: { [key: string]: any };
  key: string;
  groupKey?: string;
  isActive?: boolean;
}

export interface TableWidgetProps<T extends TableRow = TableRow> {
  columns: TableColumn<T>[];
  rows: T[];
  onContextMenu?: (event: MouseEvent, row: T) => any;
  onRowSelected?: (event: MouseEvent, row: T) => any;
  loading?: boolean;
}

@observer
export class TableWidget<T extends TableRow = TableRow> extends React.Component<TableWidgetProps<T>> {
  getColumns(): TableColumn<T>[] {
    return _.map(this.props.columns, (col) => {
      let accessor = col.accessor;

      // ensure a default accessor
      if (!accessor) {
        accessor = (event) => {
          if (event.cell == null) {
            return <S.NoValue>empty</S.NoValue>;
          }
          return `${event.cell}`;
        };
      }

      // additionally no wrap?
      let finalAccessor = accessor;
      if (col.noWrap) {
        finalAccessor = (event) => {
          return <S.NoWrap>{accessor?.(event)}</S.NoWrap>;
        };
      }

      return {
        ...col,
        accessor: finalAccessor
      };
    });
  }

  getLoader = () => {
    if (!this.props.loading) {
      return null;
    }
    return (
      <S.LoaderContainer>
        <LoaderSpinner />
      </S.LoaderContainer>
    );
  };

  render() {
    const cols = this.getColumns();

    return (
      <S.Table>
        <thead>
          <S.ColumnsRow>
            {_.map(cols, (col) => {
              return <TableColumnWidget column={col} key={col.key} />;
            })}
          </S.ColumnsRow>
        </thead>
        <tbody>
          {this.props.loading ? (
            <tr>
              <td>{this.getLoader()}</td>
            </tr>
          ) : (
            <TableRowsWidget<T>
              rows={this.props.rows.filter((f) => !f.groupKey)}
              onContextMenu={this.props.onContextMenu}
              onRowSelected={this.props.onRowSelected}
              cols={cols}
            />
          )}
        </tbody>
      </S.Table>
    );
  }
}

namespace S {
  export const Table = styled.table`
    color: ${(p) => p.theme.text.primary};
    border-spacing: 0;
    border: solid 1px ${(p) => p.theme.table.border};
    width: 100%;
    position: relative;
  `;

  export const LoaderContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    position: absolute;
    padding: 32px;
    left: 0;
    right: 0;
  `;

  export const ColumnsRow = styled.tr`
    background: rgba(0, 0, 0, 0.3);
  `;

  export const NoWrap = styled.div`
    display: flex;
    white-space: nowrap;
  `;
  export const NoValue = styled.div`
    font-style: italic;
    opacity: 0.3;
  `;
}
